/**
 * Select de categoría de gasto CON BUSCADOR. Componente shared usado en:
 *  - /admin/proveedores (asignación admin) — slugs raw.
 *  - /admin/invoices/:id (revisión de facturas) — slugs raw + labels visibles.
 *
 * shadcn no tiene Combobox vendoreado y no quiero meter `cmdk` solo para esto:
 * implementado mínimo con Popover + Input + lista filtrada.
 *
 * `formatLabel` opcional convierte el slug a un texto para mostrar (ej.
 * "vivienda_alquiler" → "Vivienda — alquiler"). Default = slug tal cual.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { CATEGORIA_GASTO } from '@shared/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export type Categoria = (typeof CATEGORIA_GASTO)[number];

/**
 * Formato amigable por defecto: "vivienda_alquiler" → "Vivienda alquiler",
 * primera letra mayúscula. Mantiene el slug si es de una sola palabra.
 */
function defaultFormatLabel(slug: string): string {
  const t = slug.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function SearchableCategorySelect({
  value,
  onChange,
  disabled,
  placeholder = 'Sin asignar',
  nullLabel = '— Sin asignar —',
  formatLabel = defaultFormatLabel,
  triggerClassName = 'w-56',
  triggerSize = 'default',
}: {
  value: Categoria | null;
  onChange: (v: Categoria | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Texto del item "null" arriba de la lista. */
  nullLabel?: string;
  /** Convierte el slug a un label legible. Default: capitalizar primera letra. */
  formatLabel?: (slug: Categoria) => ReactNode;
  triggerClassName?: string;
  triggerSize?: 'sm' | 'default';
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATEGORIA_GASTO as readonly Categoria[];
    return (CATEGORIA_GASTO as readonly Categoria[]).filter((c) =>
      c.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          size={triggerSize}
          className={`justify-between font-normal ${triggerClassName}`}
        >
          <span className={value ? '' : 'text-muted-foreground'}>
            {value ? formatLabel(value) : placeholder}
          </span>
          <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="border-b border-border p-2">
          <Input
            autoFocus
            placeholder="Buscar categoría…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8"
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-1">
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-muted"
          >
            <span>{nullLabel}</span>
            {value == null && <Check className="size-4" />}
          </button>
          {filtered.length === 0 && (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground">
              Sin coincidencias
            </div>
          )}
          {filtered.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                onChange(c);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
            >
              <span>{formatLabel(c)}</span>
              {value === c && <Check className="size-4" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
